import React from 'react';
import ReactJSONTree from 'react-json-view';
import { isString } from 'utils/helpers';
import { jsonTreeThemes } from 'utils/themes';
import { JSONTreeContainer } from './JSONTree.styled';
import { createNewKey } from './utils';

// type Props = {
//   /** Additional classes. */
//   className?: string,
//   /** Display json tree in dark mode. */
//   darkMode: boolean,
//   /** Display clicpboard options. */
//   enableClipboard: boolean,
//   /** Collapse all tree nodes. (Toggle to collpase.) */
//   collapseAll: boolean,
//   /** Hide the item count of arrays and objects. */
//   hideItemCount: boolean,
//   /** Hides trailing spaces in object key values. */
//   hideKeyTrailingSpaces: boolean,
//   /** Hides quotes in key value. */
//   hideQuotes: boolean,
//   /** Hides the root node. If onAdd is passed, root is always shown. */
//   hideRoot: boolean,
//   /** Indent width for nested objects. */
//   indentWidth: number,
//   /** Use monospace font. */
//   monospace: boolean,
//   /**
//    * Callback function when adding a value from json object. Pass in to enable adding.
//    *
//    * ```
//    * onAdd(add)
//    * ```
//    * @param { Object } add: The added changes.
//    * <pre>
//    * {
//    *   **updated_src** _(string)_: New src value.
//    *   **name** _(string)_: New var name.
//    *   **namespace** _(array)_: Namespace indicating var location.
//    *   **new_value** _(string)_: New variable value.
//    *   **existing_value** _(string)_: Existing varaible value.
//    * }
//    * </pre>
//    * @returns { void }
//    */
//   onAdd?: () => {},
//   /**
//    * Callback function when deleting a value from json object. Pass in to enable deleting.
//    *
//    * ```
//    * onDelete(delete)
//    * ```
//    * @param { Object } delete: The deleted changes.
//    * <pre>
//    * {
//    *   **updated_src** _(string)_: New src value.
//    *   **name** _(string)_: New var name.
//    *   **namespace** _(array)_: Namespace indicating var location.
//    *   **new_value** _(string)_: New variable value.
//    *   **existing_value** _(string)_: Existing varaible value.
//    * }
//    * </pre>
//    * @returns { void }
//    */
//   onDelete?: () => {},
//   /**
//    * Callback function when json object is edited. Pass in to enable editing.
//    *
//    * ```
//    * onEdit(edit)
//    * ```
//    * @param { Object } edit: The edited changes.
//    * <pre>
//    * {
//    *   **updated_src** _(string)_: New src value.
//    *   **name** _(string)_: New var name.
//    *   **namespace** _(array)_: Namespace indicating var location.
//    *   **new_value** _(string)_: New variable value.
//    *   **existing_value** _(string)_: Existing varaible value.
//    * }
//    * </pre>
//    * @returns { void }
//    */
//   onEdit?: (edit: {}) => {},
//   /** Value of root node. */
//   rootName: string,
//   /** Display the data type for each item. */
//   showDataTypes: boolean,
//   /** Display indentation lines. */
//   showIndentation: boolean,
//   /** The json to display. Parses to object if valid json string is passed. */
//   src: Object | string,
// };

/**
 * JSON Tree component extended from [react-json-view](https://www.npmjs.com/package/react-json-view).
 */

export default class JSONTree extends React.Component {
  static defaultProps = {
    collapseAll: false,
    darkMode: false,
    enableClipboard: false,
    hideItemCount: false,
    hideKeyTrailingSpaces: false,
    hideQuotes: false,
    indentWidth: 3,
    monospace: false,
    rootName: 'root',
    showDataTypes: false,
    showIndentation: false,
    hideRoot: false,
    src: {},
  };

  constructor(props) {
    super(props);
    this.state = {
      key: undefined,
    };
  }

  shouldComponentUpdate = (nextProps) => {
    const { collapseAll } = this.props;
    if (collapseAll !== nextProps.collapseAll) {
      this.createNewKey();
    }
    return true;
  };

  /**
   * Generates a new key for the component.
   * Used to re-render an expand all or collapse all items.
   * @returns { void }
   */
  createNewKey = () => {
    this.setState({ key: createNewKey() });
  };

  /**
   * parseJson
   * Helper function to parse json string.
   * @returns { Object } The parsed json.
   */
  parseJson = () => {
    const { src } = this.props;
    if (isString(src)) {
      try {
        return { src: JSON.parse(src) };
      } catch (e) {
        const { message: error } = e;
        return { src: { error } };
      }
    }
    return { src };
  };

  /**
   * handleExpandAll
   * @param { Object } field: The field to determine if collapsed.
   * @returns { Boolean } If all nodes should be expanded.
   */
  handleCollapseAll = (field) => {
    const { collapseAll, rootName, hideRoot } = this.props;
    const { name } = field;
    if (hideRoot && name === rootName) {
      return false;
    }
    return collapseAll;
  };

  render() {
    const { key } = this.state;
    const {
      className,
      collapseAll,
      darkMode,
      enableClipboard,
      hideItemCount,
      hideKeyTrailingSpaces,
      hideQuotes,
      hideRoot,
      indentWidth,
      monospace,
      onAdd,
      onDelete,
      onEdit,
      rootName,
      showDataTypes,
      showIndentation,
      ...otherProps
    } = this.props;
    const { src } = this.parseJson();

    return (
      <JSONTreeContainer
        data-test="json-tree-container"
        className={className}
        data-indentation={showIndentation}
        data-monospace={monospace}
        data-quotes={!hideQuotes}
        data-root={!hideRoot || !!onAdd}
        data-key-trailing-spaces={!hideKeyTrailingSpaces}
      >
        <ReactJSONTree
          {...otherProps}
          key={key}
          src={src}
          displayDataTypes={showDataTypes}
          displayObjectSize={!hideItemCount}
          enableClipboard={enableClipboard}
          indentWidth={indentWidth}
          name={rootName}
          onAdd={onAdd}
          onDelete={onDelete}
          onEdit={onEdit}
          shouldCollapse={this.handleCollapseAll}
          theme={darkMode ? jsonTreeThemes.dark : jsonTreeThemes.light}
        />
      </JSONTreeContainer>
    );
  }
}
